// A price book item's picture in Flowology's house style, a line drawing (pricebook.art.get, BL-109): the hero frame with
// "Light · Dark", the newest making's state as a word, what the picture shows and how many items share it
// (items that look alike share one), and for those who edit the price book "Make a new one", which asks
// what it should show and, when the picture is shared, whether for all of them or this item only (its own
// picture: pricebook.art.split). The pair there now stays until both new pictures pass their check,
// so the frame keeps showing it while a new one is made. A close match from Flowology's picture library
// waits for those who edit to "Use this one" or "Make a new one" (pricebook.art.answerMatch); until then
// customers keep the pair there now. The cost is said only to those who see cost. It shows the host's
// light or dark, and follows a change of it, until the person presses Light or Dark here.
// The same in the office (Setup › Price book) and in a chat.
import { useEffect, useState } from 'react';
import type { ViewProps } from '../host.ts';
import { PricebookArtProps as Schema } from '../pricebook-art-schemas.ts';
import type { PricebookArtProps } from '../pricebook-art-schemas.ts';
import { Action, Label, type LabelKind } from '../primitives.tsx';

type Theme = 'light' | 'dark';
const KIND: Record<PricebookArtProps['state'], LabelKind> = {
  off: 'muted',
  none: 'muted',
  naming: 'wait',
  making: 'wait',
  checking: 'wait',
  ready: 'now',
  needs_a_look: 'bad',
  proposed: 'wait',
  to_approve: 'wait',
  paused: 'wait',
  refused: 'bad',
  failed: 'bad',
};
const MOVING = new Set(['naming', 'making', 'checking']);
const cents = (c: number) => (c < 100 ? `${Math.round(c)}¢` : `$${(c / 100).toFixed(2)}`);

export function PricebookArt({
  data: first,
  host,
  name,
  pollMs = 10_000,
}: ViewProps<PricebookArtProps> & { name?: string; pollMs?: number }) {
  const chat = host.surface === 'chat';
  const [data, setData] = useState(first);
  // the host's light or dark, following it, until the person picks one here
  const [picked, setPicked] = useState<Theme | null>(null);
  const theme = picked ?? host.theme;
  const [asking, setAsking] = useState(false);
  const [request, setRequest] = useState('');
  const [busy, setBusy] = useState(false);
  const [onlyThis, setOnlyThis] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => setData(first), [first]);

  const reload = async () => {
    const a = await host.callTool('pricebook.art.get', { itemId: data.itemId });
    const parsed = a.ok ? Schema.safeParse(a.result) : null;
    if (parsed?.success) setData(parsed.data);
  };
  // while a new pair is made, the office looks again now and then
  useEffect(() => {
    if (chat || !MOVING.has(data.state) || pollMs <= 0) return;
    const t = setInterval(() => void reload(), pollMs);
    return () => clearInterval(t);
  }, [data.state, data.itemId, chat, pollMs]);

  const make = async () => {
    setBusy(true);
    setSaid(null);
    const a = await host.callTool(split ? 'pricebook.art.split' : 'pricebook.art.regenerate', {
      itemId: data.itemId,
      ...(request.trim() ? { request: request.trim() } : {}),
    });
    setBusy(false);
    if (!a.ok) return setSaid(a.message);
    setAsking(false);
    setRequest('');
    setOnlyThis(false);
    await reload();
  };

  const answer = async (use: boolean) => {
    setBusy(true);
    setSaid(null);
    const a = await host.callTool('pricebook.art.answerMatch', { itemId: data.itemId, use });
    setBusy(false);
    if (!a.ok) return setSaid(a.message);
    await reload();
  };

  const what = name ?? 'the item';
  const sub = data.subject;
  const others = sub && !sub.own ? sub.sharedBy - 1 : 0;
  const split = others > 0 && onlyThis;
  const live = data.live[theme];
  // a dark version is made only from a passed light one, so until then the light one stands in
  const next = data.next?.[theme] ?? data.next?.light ?? null;
  // a picture that hasn't passed is shown only to those who edit (the server sends its link only to them)
  const shown = live?.url ?? next?.url ?? null;
  const notLive = !live?.url && !!next?.url;
  return (
    <article className="flw-art" aria-label={`Picture of ${what}`}>
      <div className="flw-art-head">
        <div className="flw-seg" role="group" aria-label="Which version">
          {(['light', 'dark'] as const).map((t) => (
            <button key={t} type="button" aria-pressed={theme === t} onClick={() => setPicked(t)}>
              {t === 'light' ? 'Light' : 'Dark'}
            </button>
          ))}
        </div>
        <Label kind={KIND[data.state]}>{data.words}</Label>
      </div>
      {shown ? (
        <img className="flw-art-pic" src={shown} alt={`${what}, its ${theme} picture`} />
      ) : (
        <p className="flw-empty flw-art-none" role="status">
          {data.state === 'off'
            ? 'Price book pictures are off.'
            : MOVING.has(data.state)
              ? 'Its picture shows here once it’s checked.'
              : 'No picture yet. Customers see the item by its name.'}
        </p>
      )}
      {sub && (
        <p className="flw-sub">
          {sub.look === 'label' ? 'Stands for: ' : 'Shows: '}
          {sub.words}
          {sub.own
            ? '. Its own picture.'
            : others > 0
              ? `. Shared with ${others} other item${others === 1 ? '' : 's'} that look${others === 1 ? 's' : ''} alike.`
              : '.'}
        </p>
      )}
      {notLive && <p className="flw-sub">Not live yet: customers don’t see this one.</p>}
      {data.state === 'to_approve' && data.canRequest && (
        <p className="flw-sub">Approve it, or turn it down, in Setup › Price book › Pictures.</p>
      )}
      {live && MOVING.has(data.state) && (
        <p className="flw-sub">A new one is on its way; this one shows until it’s checked.</p>
      )}
      {data.why && <p className="flw-sub">{data.why}</p>}
      {data.canRequest && data.state === 'proposed' ? (
        <div className="flw-acts">
          <button
            type="button"
            className="flw-btn flw-btn-pri"
            disabled={busy}
            onClick={() => void answer(true)}
          >
            Use this one
          </button>
          <Action onClick={() => void answer(false)}>Make a new one</Action>
        </div>
      ) : (
        data.canRequest &&
        (asking ? (
          <div className="flw-art-ask">
            {others > 0 && (
              <div className="flw-seg" role="group" aria-label="Which items get it">
                <button type="button" aria-pressed={!onlyThis} onClick={() => setOnlyThis(false)}>
                  All {others + 1} items
                </button>
                <button type="button" aria-pressed={onlyThis} onClick={() => setOnlyThis(true)}>
                  Only this one
                </button>
              </div>
            )}
            <label className="flw-fld">
              <span>What should it show?</span>
              <textarea
                className="flw-field flw-art-text"
                value={request}
                maxLength={300}
                rows={3}
                placeholder="Leave it empty for the house style"
                onChange={(e) => setRequest(e.target.value)}
              />
            </label>
            <div className="flw-acts">
              <button
                type="button"
                className="flw-btn flw-btn-pri"
                disabled={busy}
                onClick={() => void make()}
              >
                {busy ? 'Asking…' : 'Make it'}
              </button>
              <Action onClick={() => setAsking(false)}>Cancel</Action>
            </div>
          </div>
        ) : (
          <div className="flw-acts">
            <Action onClick={() => setAsking(true)}>Make a new one</Action>
          </div>
        ))
      )}
      {said && (
        <p className="flw-bad" role="alert">
          {said}
        </p>
      )}
      {data.canRequest && data.estimateCents != null && (
        <p className="flw-sub">
          About {cents(data.estimateCents)} a pair. The pictures there now stay until both new ones pass
          {others > 0 ? ', on every item sharing them.' : '.'}
        </p>
      )}
    </article>
  );
}
